import { DOCUMENT } from '@angular/common';
import {
  Injectable,
  LOCALE_ID,
  Renderer2,
  RendererFactory2,
  computed,
  inject,
  signal,
} from '@angular/core';
import { LocaleId } from '../../models/locale/locale-id.enum';
import { TranslationDictionaryMapping } from '../../models/translation-dictionary/translation-dictionary-mapping.type';
import { TranslationDictionary } from '../../models/translation-dictionary/translation-dictionary.type';
import { mergeTranslationDictionaryMappings } from '../../utils/translation-dictionary/translation-dictionary.utils';

@Injectable({ providedIn: 'root' })
export class TranslationDictionaryService<
  T extends TranslationDictionary = TranslationDictionary,
> {
  // --- Dependencies (using `inject` for tree-shakable providers) ---
  private readonly rendererFactory2 = inject(RendererFactory2);
  private readonly renderer: Renderer2 = this.rendererFactory2.createRenderer(
    null,
    null,
  );
  private readonly document = inject(DOCUMENT);

  // --- Signals (state) ---
  private readonly _localeId = signal<LocaleId>(inject(LOCALE_ID) as LocaleId);
  private readonly _translationDictionaryMapping = signal<
    TranslationDictionaryMapping<T>
  >({});

  private readonly _translationDictionary = computed<T | undefined>(() => {
    const localeId = this._localeId();
    const translationDictionaryMapping = this._translationDictionaryMapping();
    return translationDictionaryMapping[localeId];
  });

  constructor() {
    this.applyHtmlLang(this._localeId());
  }

  protected applyHtmlLang(locale: string): void {
    this.renderer.setAttribute(this.document.documentElement, 'lang', locale);
  }

  /**
   * Register/merge one or more dictionary mappings.
   */
  registerTranslationDictionaryMapping(
    ...translationDictionaryMapping: TranslationDictionaryMapping<T>[]
  ): void {
    const merged = mergeTranslationDictionaryMappings(
      this._translationDictionaryMapping(),
      ...translationDictionaryMapping,
    );

    this._translationDictionaryMapping.set(merged);
  }

  //Getters
  get translationDictionary(): T | undefined {
    return this._translationDictionary();
  }
}
